import _ from "../../../../../../@lodash/@lodash";
import useThemeMediaQuery from "@fuse/hooks/useThemeMediaQuery";
import { useEffect, useState } from "react";
import utilityService from "@fuse/services/utilityService";
import petService from "@fuse/services/petService";
import { useAppDispatch, useAppSelector } from "app/store";
import { useNavigate, useParams, useLocation } from "react-router";
import { useSearchParams } from "react-router-dom";
import moment from "moment";
import { IPetModel, IVetModel } from "@fuse/models";
import UpdatePetForm from "./UpdatePetProfileForm";
import { PetUpdateType } from "../../types/PetUpdateType";
import { selectUser } from "app/store/user/userSlice";
import PetOwnerApp from "./PetOwnerApp";
import PetVetsListing from "./PetVetsListing";
import vetService from "@fuse/services/vetService";
import userService from "@fuse/services/userService";
import { Card } from "@mui/material";
import FuseLoading from "@fuse/core/FuseLoading";
import BackupContacts from "../../../dashboard/components/backup-contacts";
import ConnectWithUs from "../../../dashboard/components/connect-with-us";
import { BackupContactType } from "../../../dashboard/types/TeamMemberType";

function UpdatePetProfileApp() {
	const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down("lg"));
	const user = useAppSelector(selectUser);
	const navigator = useNavigate();
	const location = useLocation();
	const param = useParams();
	const [searchParams] = useSearchParams();
	const isRecoverMode = location.pathname.includes("recover/start");
	const id = isRecoverMode ? searchParams.get("pet_id") : param.id;
	const [breeds, setBreeds] = useState([]);
	const [allSpecies, setSpecies] = useState([]);
	const [allBrands, setBrands] = useState([]);
	const [pet, setPet] = useState<IPetModel>({} as IPetModel);
	const [vets, setVets] = useState<IVetModel[]>([] as IVetModel[]);
	const [contacts, setContacts] = useState<BackupContactType[]>([]);
	const [owner, setOwner] = useState<any>(null);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(false);
	const [processing, setProcessing] = useState(false);

	useEffect(() => {
		utilityService
			.getStaticData()
			.then((res: any) => {
				if (res.result) {
					setSpecies(res.result.species);
					setBrands(res.result.brands);
				}
			})
			.catch((error) => {
				console.log(error);
			});

		if (!isRecoverMode) {
			utilityService
				.getPetBreedsByPetId(id)
				.then((res: any) => {
					if (res.result) {
						setBreeds(res.result);
					}
				})
				.catch((error) => {
					console.log(error);
				});
		}

		if (isRecoverMode) {
			petService
				.getRecoverDetails(id)
				.then((res: any) => {
					if (res) {
						const petData = res.pet;
						if (petData.image) {
							petData.imageUrl = `${process.env.VITE_APP_API_BASE_URL}Pet/pet-images/${petData.image}`;
						}
						if (petData.dob) {
							petData.dob = new Date(petData.dob);
						}
						if (petData.lastRabiesVaccine) {
							petData.lastRabiesVaccine = new Date(petData.lastRabiesVaccine);
						}
						setPet(petData);

						const ownerData = res.owner;
						setOwner({
							data: {
								displayName: `${ownerData.firstName || ""} ${ownerData.lastName || ""}`.trim(),
								email: ownerData.email,
								address1: ownerData.address1,
								address2: ownerData.address2,
								city: ownerData.city,
								state: ownerData.state,
								zipcode: ownerData.zipcode,
								phoneNumber: ownerData.phoneNumber,
								photoUrl: null,
							},
						});

						if (res.sites) {
							setVets(res.sites);
						}
					}
				})
				.catch((error: any) => {
					const msg = Array.isArray(error) ? error.join("<br />") : String(error);
					setError(msg);
					setTimeout(() => setError(""), 10000);
				});

			userService.getBackupContacts()
				.then((res: any) => { if (res) setContacts(res); })
				.catch(() => setContacts([]));
		} else {
			petService
				.getPetById(id)
				.then((res: any) => {
					if (res.result) {
						const pet = res.result;
						if (pet.image) {
							pet.imageUrl = `${process.env.VITE_APP_API_BASE_URL}Pet/pet-images/${pet.image}`;
						}
						if (pet.dob) {
							pet.dob = new Date(pet.dob);
						}
						if (pet.lastRabiesVaccine) {
							pet.lastRabiesVaccine = new Date(pet.lastRabiesVaccine);
						}
						setPet(pet);
					}
				})
				.catch((error: string[]) => {
					setError(error.join("<br />"));
					setTimeout(() => {
						setError("");
					}, 10000);
				});

			vetService
				.getSitesForPet(id)
				.then((res: any) => {
					if (res) {
						setVets(res);
					}
				})
				.catch((error) => {
					setVets([]);
				});
		}
	}, []);

	function updatePet(data: PetUpdateType) {
		if (isRecoverMode) return;
		setProcessing(true);
		data.id = id;
		if (!data.dob) {
			data.dob = null;
		} else {
			data.dob = moment(data.dob).format("YYYY-MM-DD");
		}

		if (!data.lastRabiesVaccine) {
			data.lastRabiesVaccine = null;
		} else {
			data.lastRabiesVaccine = moment(data.lastRabiesVaccine).format(
				"YYYY-MM-DD"
			);
		}

		data.breeds = [];

		if (data.petBreedList?.length > 0) {
			data.petBreedList.forEach((f) => {
				data.breeds.push({
					id: f,
					name: f,
				});
			});
		}

		data.image = data.imageUrl?.indexOf(";") > 0 ? data.imageUrl : pet.image;

		petService
			.updatePetProfile(data)
			.then((res: IPetModel) => {
				setProcessing(false);
				if (res.id) {
					navigator(`/`);
				}
			})
			.catch((error: string[]) => {
				setProcessing(false);
				setError(error.join("<br />"));

				setTimeout(() => {
					setError("");
				}, 10000);
			});
	}

	const displayUser = isRecoverMode ? owner : user;

	if (!pet.id)
		return (
			<div className="w-full h-full">
				{<FuseLoading />}
			</div>
		);
	else
		return (
			<div className="w-full p-[20px]">
				<div className="flex flex-col gap-[20px]">
					<Card className="p-[18px] md:p-[28px]">
						{pet.id && (
							<UpdatePetForm
								key={pet.id}
								pet={pet}
								breeds={breeds}
								allSpecies={allSpecies}
								allBrands={allBrands}
								onUpdate={updatePet}
								loading={loading}
								processing={processing}
								isRecoverMode={isRecoverMode}
								recoverEmail={isRecoverMode ? owner?.data?.email ?? "" : ""}
							/>
						)}
					</Card>
				</div>

				{/* Non-recover: owner (left) + vets (right) */}
				{!isRecoverMode && (
					<div className="flex flex-col md:flex-row gap-[20px]">
						<div className="flex w-full md:w-1/2 gap-[20px] mt-12">
							<Card className="p-[18px] md:p-[28px] w-full md:h-[14em]">
								{displayUser?.data && (
									<PetOwnerApp key={displayUser.data?.displayName} user={displayUser} />
								)}
							</Card>
						</div>
						<div className="flex w-full md:w-1/2 gap-[20px] mt-12">
							<Card className="p-[18px] md:p-[28px] w-full">
								{vets && <PetVetsListing vets={vets} setVets={setVets} pet={pet} />}
							</Card>
						</div>
					</div>
				)}

				{/* Recover mode: vets (left) + backup contacts (right), then connect with us */}
				{isRecoverMode && (
					<>
						<div className="flex flex-col md:flex-row gap-[20px]">
							<div className="flex w-full md:w-1/2 gap-[20px] mt-12">
								<Card className="p-[18px] md:p-[28px] w-full">
									{vets && (
										<PetVetsListing
											vets={vets}
											setVets={setVets}
											pet={pet}
											isRecoverMode={true}
										/>
									)}
								</Card>
							</div>
							<div className="flex w-full md:w-1/2 gap-[20px] mt-12">
								<Card className="p-[18px] md:p-[28px] w-full">
									<BackupContacts
										contacts={contacts}
										fetchContacts={() =>
											userService.getBackupContacts().then((res: any) => {
												if (res) setContacts(res);
											})
										}
									/>
								</Card>
							</div>
						</div>
						<div className="flex w-full gap-[20px] mt-12">
							<Card className="p-[18px] md:p-[28px] w-full">
								<ConnectWithUs />
							</Card>
						</div>
					</>
				)}
			</div>
		);
}

export default UpdatePetProfileApp;
